<template>
  <div class="report-container">
    <!-- Page 1 -->
    <report-page1
      class="report-page"
      :project-data="projectData"
      :include-sections="includeSections"
      :payment-type="paymentType"
    />

    <!-- Page 2 -->
    <report-page2
      v-if="shouldShowPage2"
      class="report-page"
      :project-data="projectData"
      :include-sections="includeSections"
      :consumption-data="consumptionData"
      :selected-panels="selectedPanels"
      :selected-inverters="selectedInverters"
      :selected-batteries="selectedBatteries"
      :selectedEVChargers="selectedEVChargers"
      :selected-meters="selectedMeters"
      :payment-type="paymentType"
      :get-panel-device-count="getPanelDeviceCount"
      :get-inverter-device-count="getInverterDeviceCount"
      :get-battery-device-count="getBatteryDeviceCount"
      :getEVChargerDeviceCount="getEVChargerDeviceCount"
      :get-meter-device-count="getMeterDeviceCount"
    />

    <!-- Page 3 -->
    <report-page3
      v-if="shouldShowPage3"
      class="report-page"
      :project-data="projectData"
      :include-sections="includeSections"
      :payment-type="paymentType"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, ComputedRef } from "vue";
import type {
  Project,
  Panel,
  Inverter,
  Battery,
  EVCharger,
  Meter,
} from "@/types/projectTypes";
import ReportPage1 from "./pages/ReportPage1.vue";
import ReportPage2 from "./pages/ReportPage2.vue";
import ReportPage3 from "./pages/ReportPage3.vue";

export default defineComponent({
  name: "ReportComponent",
  components: {
    ReportPage1,
    ReportPage2,
    ReportPage3,
  },
  props: {
    projectData: {
      type: Object as PropType<Project | null>,
      required: true,
    },
    includeSections: {
      type: Object as PropType<Record<string, boolean>>,
      required: true,
    },
    consumptionData: {
      type: Object as PropType<{
        totalF1: number;
        totalF2: number;
        totalF3: number;
        grandTotal: number;
      }>,
      required: true,
    },
    selectedPanels: {
      type: Array as PropType<Panel[]>,
      required: true,
    },
    selectedInverters: {
      type: Array as PropType<Inverter[]>,
      required: true,
    },
    selectedBatteries: {
      type: Array as PropType<Battery[]>,
      required: true,
    },
    selectedEVChargers: {
      type: Array as PropType<EVCharger[]>,
      required: true,
    },
    selectedMeters: {
      type: Array as PropType<Meter[]>,
      required: true,
    },
    paymentType: {
      type: String,
      required: true,
    },
    getPanelDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getInverterDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getBatteryDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getEVChargerDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getMeterDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
  },
  setup(props) {
    const shouldShowPage2 = computed(() => {
      // Always show page 2 for complete_solution
      if (props.projectData?.service_type === "complete_solution") {
        return true;
      }

      // For other service types, show if there are plant components or price/signature
      return (
        props.includeSections.panels ||
        props.includeSections.inverters ||
        props.includeSections.batteries ||
        props.includeSections.evChargers ||
        props.includeSections.meters ||
        props.includeSections.prices
      );
    });

    const shouldShowPage3 = computed(() => {
      // Show page 3 for complete_solution with installment or one_time payment
      return (
        props.projectData?.service_type === "complete_solution" &&
        (props.paymentType === "installment" || props.paymentType === "one_time")
      );
    });

    return {
      shouldShowPage2,
      shouldShowPage3,
    };
  },
});
</script>

<style scoped>
.report-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.report-page {
  width: 210mm; /* A4 width */
  min-height: 297mm; /* A4 height */
  background: var(--color-white);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--color-light-gray);
  margin: 0 auto;
  position: relative;
}

@media print {
  .report-container {
    padding: 0;
    gap: 0;
  }

  .report-page {
    box-shadow: none;
    border: none;
    page-break-after: always;
  }
}
</style>
